import React from 'react';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { FILTER_TYPES_KEYS } from '../../../constants/appraisal-comparison-filter-types';
import constants from '@shared-styles/constants.module.scss';
import NotEnoughDataMessage from '../../shared/components/NotEnoughDataMessage';
import { getXAxisLabel, getYAxisLabel } from '../../shared/components/GraphLabels';

/**
 * @typedef {object} AggregatePercentageOrgVsIndChartProps
 * @property {Array<*>} [scaledData]
 * @property {number} [total]
 * @property {number} [industryTotal]
 * @property {(data: import('recharts').BarRectangleItem, dataKey: string) => void} handleAggregatePercentageClick
 */

/**
 * @param {AggregatePercentageOrgVsIndChartProps} props
 */
const AggregatePercentageOrgVsIndChart = props => {
  const { scaledData, total, industryTotal, handleAggregatePercentageClick } = props;

  const chartNameKey = [
    { chartName: 'Industry Aggregates', dataKey: FILTER_TYPES_KEYS.Industry },
    { chartName: 'Organization Aggregates', dataKey: FILTER_TYPES_KEYS.Organization }
  ];

  const xAxisTickFormatter = (tick, industryTotal) => {
    if (tick == 0) {
      return `0%`;
    } else if (tick == (industryTotal * 25) / 100) {
      return `25%`;
    } else if (tick == (industryTotal * 50) / 100) {
      return `50%`;
    } else if (tick == (industryTotal * 75) / 100) {
      return `75%`;
    } else if (tick == industryTotal) {
      return `100%`;
    } else {
      return `${tick}%`;
    }
  };

  return (
    <div onClick={e => e.stopPropagation()} data-testid='aggregate-percentage-chart-org-vs-ind'>
      {total <= 2 ? <NotEnoughDataMessage /> : null}
      <ResponsiveContainer width='95%' height={400}>
        <ComposedChart
          layout='vertical'
          width={500}
          height={400}
          data={scaledData}
          margin={{
            top: 20,
            right: 20,
            bottom: 20,
            left: 40
          }}>
          <CartesianGrid stroke='#1D2D5C' strokeDasharray='2 2' strokeOpacity='.6' />
          <XAxis
            domain={[0, industryTotal]}
            allowDecimals={true}
            type='number'
            ticks={[
              0,
              (industryTotal * 25) / 100,
              (industryTotal * 50) / 100,
              (industryTotal * 75) / 100,
              industryTotal
            ]}
            tickFormatter={tick => xAxisTickFormatter(tick, industryTotal)}>
            {getXAxisLabel('# Appraisals containing PA')}
          </XAxis>
          <YAxis dataKey='range' type='category' scale='band'>
            {getYAxisLabel('Aggregate PA % Distribution in Quintiles', 50, 20)}
          </YAxis>
          <Tooltip
            formatter={(value, name) =>
              name == 'Organization Aggregates'
                ? Math.round((value * total) / industryTotal)
                : value
            }
          />
          <Legend wrapperStyle={{ left: '70px', paddingTop: '1.5rem' }} />
          {total >= 3 && (
            <Bar
              name={chartNameKey[0].chartName}
              dataKey={chartNameKey[0].dataKey}
              barSize={20}
              fill={constants.industryColor}
              cursor='pointer'
              onClick={data => handleAggregatePercentageClick(data, chartNameKey[0].dataKey)}
            />
          )}
          {total >= 3 && (
            <Bar
              name={chartNameKey[1].chartName}
              dataKey={chartNameKey[1].dataKey}
              barSize={20}
              fill={constants.organizationColor}
              cursor='pointer'
              onClick={data => handleAggregatePercentageClick(data, chartNameKey[1].dataKey)}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AggregatePercentageOrgVsIndChart;
